import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import { useState } from 'react';
import * as moment from 'moment/moment';
import type { NewPortalToken, PortalToken } from '../../../../../shared/publicPortal/types';
import type { ExpirationOption, ReleaseGroupOption } from './types';
import MutatePortalTokenModal from './components/MutatePortalTokenModal';
import { useAddToast } from '../../../../contexts/ToastContext';

type Props = {
  isEditing: boolean;
  errors: string[] | null;
  onHide: () => void;
  onEditToken: (id: number, newPortalToken: NewPortalToken) => Promise<void>;
  editingToken: PortalToken;
};

const ModalEditPortalToken: VoidFunctionComponent<Props> = ({
  isEditing,
  errors,
  onHide,
  onEditToken,
  editingToken,
}) => {
  const [newName, setNewName] = useState<string>(editingToken.name);
  const addToast = useAddToast();
  const [selectedExpirationOption, setSelectedExpirationOption] = useState<ExpirationOption | null>(null);
  const [selectedReleaseGroups, setSelectedReleaseGroups] = useState<ReleaseGroupOption[]>(
    editingToken.releaseGroups.map(({ id, title }) => ({
      value: id,
      label: title,
    })),
  );
  // We are defaulting the expiration to the existing expiration date, so we don't accidentally extend it.
  let expiration: Date | null = new Date(editingToken.expiration || '');
  // Allow user to extend expiration date.
  if (selectedExpirationOption?.value) {
    const { numerator, unit } = selectedExpirationOption.value;
    expiration = moment().add(numerator, unit).toDate();
  }
  // If expiration is set to 'Never', set expiration to null.
  if (selectedExpirationOption?.label === 'Never') {
    expiration = null;
  }

  const onEditExistingToken = async () => {
    try {
      await onEditToken(editingToken.id, {
        name: newName,
        expiration,
        releaseGroups: selectedReleaseGroups.map(({ value, label }) => {
          const id = _.toNumber(value);
          return { id, title: label as string };
        }),
      });
      onHide();
    } catch (error) {
      addToast({ type: 'error', content: 'Failed to edit portal token' });
    }
  };

  return (
    <MutatePortalTokenModal
      shouldShowModal
      errors={errors || []}
      name={newName}
      setName={setNewName}
      expiration={expiration}
      expirationOption={selectedExpirationOption || undefined}
      setExpirationOption={setSelectedExpirationOption}
      selectedReleaseGroups={selectedReleaseGroups}
      setSelectedReleaseGroups={setSelectedReleaseGroups}
      onHide={onHide}
      onMutateToken={onEditExistingToken}
      type="Edit"
      isSaving={isEditing}
    />
  );
};

export default ModalEditPortalToken;
